"use client";
import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, } from "react";
const CommandRegistryContext = createContext(null);
export function CommandRegistryProvider({ children, }) {
    const [actions, setActions] = useState([]);
    const [sources, setSources] = useState([]);
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const openPalette = useCallback((next = "") => {
        setQuery(next);
        setOpen(true);
    }, []);
    const registerActions = useCallback((next) => {
        setActions((current) => [...current, ...next]);
        const ids = new Set(next.map((action) => action.id));
        return () => setActions((current) => current.filter((action) => !ids.has(action.id)));
    }, []);
    const registerSource = useCallback((source) => {
        setSources((current) => [...current, source]);
        return () => setSources((current) => current.filter((item) => item.id !== source.id));
    }, []);
    const value = useMemo(() => ({
        actions,
        sources,
        open,
        query,
        setOpen,
        setQuery,
        openPalette,
        registerActions,
        registerSource,
    }), [
        actions,
        sources,
        open,
        query,
        openPalette,
        registerActions,
        registerSource,
    ]);
    return (_jsx(CommandRegistryContext.Provider, { value: value, children: children }));
}
export function useCommandRegistry() {
    const context = useContext(CommandRegistryContext);
    if (context === null) {
        throw new Error("useCommandRegistry must be used inside <CommandRegistryProvider>");
    }
    return context;
}
/**
 * Contribute commands for as long as the calling component is mounted, so the
 * palette always reflects what is actually actionable on the current screen.
 */
export function useRegisterCommands(actions) {
    const { registerActions } = useCommandRegistry();
    const ref = useRef(actions);
    ref.current = actions;
    const key = JSON.stringify(actions.map((action) => [
        action.id,
        action.label,
        action.description,
        action.group,
        action.keywords,
        action.shortcut,
        action.destructive,
    ]));
    useEffect(() => registerActions(ref.current.map((action) => ({
        ...action,
        perform: () => ref.current.find((item) => item.id === action.id)?.perform(),
    }))), [key, registerActions]);
}
export function useRegisterCommandSource(source) {
    const { registerSource } = useCommandRegistry();
    const ref = useRef(source);
    ref.current = source;
    useEffect(() => registerSource({
        id: ref.current.id,
        group: ref.current.group,
        search: (query, signal) => ref.current.search(query, signal),
    }), [source.id, registerSource]);
}
//# sourceMappingURL=command-registry.js.map